import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, getDocs } from 'firebase/firestore';
import styles from './CategoryPage.module.css';
import heroStyles from '../Components/Hero.module.css';
import { timeAgo } from '../Dashboard/timeUtils';
import { Search } from 'lucide-react';
import Footer from '../Components/Footer';

const CategoryPage = ({ lang = 'EN' }) => {
  const { categoryName } = useParams();
  const [newsList, setNewsList] = useState([]);
  const [filteredNews, setFilteredNews] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search & Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('All');
  const [selectedMonth, setSelectedMonth] = useState('All');
  const [dateFilterType, setDateFilterType] = useState('all');

  // Decode URL category name properly
  const decodedCategory = categoryName ? decodeURIComponent(categoryName).trim() : '';

  // Comprehensive Category names translation mapping for UI Headings
  const getCategoryTitle = (cat) => {
    if (!cat) return lang === 'EN' ? "Category News" : "زمرہ کی خبریں";
    const lowerCat = cat.toLowerCase();
    
    // Column Categories
    if (lowerCat.includes('column') || lowerCat.includes('aaj ka column') || lowerCat.includes('columns')) {
      return lang === 'EN' ? "Aaj Ka Column" : "آج کا کالم";
    }
    // Ishtiharat / Ads
    if (lowerCat.includes('ishtiharat') || lowerCat.includes('advertisement') || lowerCat.includes('ads')) {
      return lang === 'EN' ? "Advertisements (Ishtiharat)" : "اشتہارات";
    }
    // City / Shehar
    if (lowerCat.includes('shehar') || lowerCat.includes('city')) {
      return lang === 'EN' ? "City News (Shehar)" : "شہر کی خبریں";
    }
    // Health / Sehat
    if (lowerCat.includes('sehat') || lowerCat.includes('health')) {
      return lang === 'EN' ? "Health (Sehat)" : "صحت";
    }
    // Sports / Khel
    if (lowerCat.includes('khel') || lowerCat.includes('sports')) {
      return lang === 'EN' ? "Sports (Khel)" : "کھیل";
    }
    // Showbiz / Entertainment
    if (lowerCat.includes('shobiz') || lowerCat.includes('showbiz') || lowerCat.includes('entertainment')) {
      return lang === 'EN' ? "Showbiz" : "شوبز";
    }
    // Business / Karobar
    if (lowerCat.includes('business') || lowerCat.includes('karobar') || lowerCat.includes('maeeshat')) {
      return lang === 'EN' ? "Business" : "کاروبار و معیشت";
    }
    // Politics / Siasat
    if (lowerCat.includes('siasat') || lowerCat.includes('politics')) {
      return lang === 'EN' ? "Politics" : "سیاست";
    }
    // Tech / Technology
    if (lowerCat.includes('tech') || lowerCat.includes('technology')) {
      return lang === 'EN' ? "Technology" : "ٹیکنالوجی";
    }
    // International / World
    if (lowerCat.includes('bain') || lowerCat.includes('international') || lowerCat.includes('world')) {
      return lang === 'EN' ? "International" : "بین الاقوامی";
    }
    // National / Qaumi
    if (lowerCat.includes('qaumi') || lowerCat.includes('pakistan') || lowerCat.includes('national')) {
      return lang === 'EN' ? "National News" : "قومی خبریں";
    }
    
    // Fallback if category is not listed above
    return cat;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchCategoryNews = async () => {
      setLoading(true);
      try {
        const querySnapshot = await getDocs(collection(db, "news"));
        let allNews = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const matchedNews = allNews.filter(item => {
          if (!item.newsCategory) return false;
          const itemCat = item.newsCategory.trim().toLowerCase();
          const targetCat = decodedCategory.toLowerCase();
          
          return (
            itemCat === targetCat ||
            itemCat.includes(targetCat) ||
            targetCat.includes(itemCat)
          );
        });

        matchedNews.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        setNewsList(matchedNews);
        setFilteredNews(matchedNews);
      } catch (err) {
        console.error("Error fetching category news:", err);
      } finally {
        setLoading(false);
      }
    };

    if (decodedCategory) {
      fetchCategoryNews();
    }
  }, [decodedCategory]);

  // Filtering Logic
  useEffect(() => {
    let result = newsList;

    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      result = result.filter(item => 
        (item.titleEn && item.titleEn.toLowerCase().includes(term)) ||
        (item.titleUr && item.titleUr.toLowerCase().includes(term))
      );
    }

    const now = new Date();
    if (dateFilterType === 'today') {
      result = result.filter(item => {
        if (!item.timestamp) return false;
        const itemDate = new Date(item.timestamp);
        return itemDate.toDateString() === now.toDateString();
      });
    } else if (dateFilterType === 'week') {
      result = result.filter(item => {
        if (!item.timestamp) return false;
        const itemDate = new Date(item.timestamp);
        const diffTime = Math.abs(now - itemDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= 7;
      });
    }

    if (selectedYear !== 'All') {
      result = result.filter(item => {
        const dateStr = item.date || item.timestamp;
        if (!dateStr) return false;
        const itemYear = new Date(dateStr).getFullYear().toString();
        return itemYear === selectedYear;
      });
    }

    if (selectedMonth !== 'All') {
      result = result.filter(item => {
        const dateStr = item.date || item.timestamp;
        if (!dateStr) return false;
        const itemMonth = (new Date(dateStr).getMonth() + 1).toString();
        return itemMonth === selectedMonth;
      });
    }

    setFilteredNews(result);
  }, [searchTerm, selectedYear, selectedMonth, dateFilterType, newsList]);

  const years = ['All', '2026', '2025', '2024', '2023'];
  const months = [
    { label: lang === 'EN' ? 'All Months' : 'تمام مہینے', value: 'All' },
    { label: lang === 'EN' ? 'January' : 'جنوری', value: '1' }, 
    { label: lang === 'EN' ? 'February' : 'فروری', value: '2' },
    { label: lang === 'EN' ? 'March' : 'مارچ', value: '3' }, 
    { label: lang === 'EN' ? 'April' : 'اپریل', value: '4' },
    { label: lang === 'EN' ? 'May' : 'مئی', value: '5' }, 
    { label: lang === 'EN' ? 'June' : 'جون', value: '6' },
    { label: lang === 'EN' ? 'July' : 'جولائی', value: '7' }, 
    { label: lang === 'EN' ? 'August' : 'اگست', value: '8' },
    { label: lang === 'EN' ? 'September' : 'ستمبر', value: '9' }, 
    { label: lang === 'EN' ? 'October' : 'اکتوبر', value: '10' },
    { label: lang === 'EN' ? 'November' : 'نومبر', value: '11' }, 
    { label: lang === 'EN' ? 'December' : 'دسمبر', value: '12' },
  ];

  return (
    <>
    <div className={styles.container} dir={lang === 'UR' ? 'rtl' : 'ltr'}>
      
      {/* Dynamic Urdu Translated Heading */}
      <h2 className={styles.title}>
        {getCategoryTitle(decodedCategory)}
      </h2>

      {/* Search and Filter Section */}
      <div className={styles.filterSection}>
        <div className={styles.searchBox}>
          <Search size={18} className={styles.searchIcon} style={{ [lang === 'EN' ? 'left' : 'right']: '15px' }} />
          <input 
            type="text"
            placeholder={lang === 'EN' ? "Search news by title..." : "عنوان سے خبر تلاش کریں..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
            style={{ paddingLeft: lang === 'EN' ? '45px' : '15px', paddingRight: lang === 'UR' ? '45px' : '15px' }}
          />
        </div>

        <div className={styles.filtersRow}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>{lang === 'EN' ? "Timeframe" : "وقت کا دورانیہ"}</label>
            <select value={dateFilterType} onChange={(e) => setDateFilterType(e.target.value)} className={styles.filterSelect}>
              <option value="all">{lang === 'EN' ? 'All Time' : 'تمام وقت'}</option>
              <option value="today">{lang === 'EN' ? 'Today' : 'آج کی خبریں'}</option>
              <option value="week">{lang === 'EN' ? 'Past 7 Days' : 'پچھلے 7 دن'}</option>
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>{lang === 'EN' ? "Month" : "مہینہ"}</label>
            <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className={styles.filterSelect}>
              {months.map((m, idx) => (
                <option key={idx} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>{lang === 'EN' ? "Year" : "سال"}</label>
            <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className={styles.filterSelect}>
              {years.map((yr, idx) => (
                <option key={idx} value={yr}>{yr === 'All' ? (lang === 'EN' ? 'All' : 'تمام') : yr}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      

      {loading ? (
        <h3 className={styles.statusMessage}>
          {lang === 'EN' ? "Loading news..." : "خبریں لوڈ ہو رہی ہیں..."}
        </h3>
      ) : filteredNews.length === 0 ? (
        <p className={styles.statusMessage}>
          {lang === 'EN' ? "No matching news found." : "آپ کی تلاش کے مطابق کوئی خبر نہیں ملی۔"}
        </p>
      ) : (
        <div className={styles.newsGrid}>
          {filteredNews.map(item => (
            <Link to={`/news/${item.id}`} key={item.id} className={styles.newsCard}>
              {item.randomHeroImage && (
                <img src={item.randomHeroImage} alt="news" className={styles.newsCardImg} />
              )}
              <div className={styles.newsCardBody}>
                {item.newsCategory && (
                  <span className={heroStyles.categoryBadge}>
                    {item.newsCategory}
                  </span>
                )}
                <h4 className={styles.newsCardTitle}>
                  {lang === 'EN' ? item.titleEn : item.titleUr}
                </h4>
                <div className={styles.newsCardMeta}>
                  {item.date && <span>{item.date} • </span>}
                  <span>{timeAgo(item.timestamp)}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
    
    </>
  );
};

export default CategoryPage;